import { useCallback, useEffect, useRef } from 'react'
import useFocusMemory, { getVisibleFocusableElements, isKeyboardEditableElement } from './useKeyboardNavigation'
import useDirectionalRegionNavigation, { createLinearArrowKeyHandler } from './useDirectionalRegionNavigation'

const DEFAULT_CLICKABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Centralized directional keyboard navigation for Wings app nav + roadmap content regions.
 * Keeps sidebar/top/content traversal logic out of the component render body.
 *
 * @param {{
 *  sidebarRef: React.MutableRefObject<HTMLElement|null>,
 *  topBarRef: React.MutableRefObject<HTMLElement|null>,
 *  sidebarToggleRef: React.MutableRefObject<HTMLElement|null>,
 *  sidebarState: string,
 * }} params
 * @returns {{
 *  handleSidebarKeyDown: (e: React.KeyboardEvent) => void,
 *  handleTopBarKeyDown: (e: React.KeyboardEvent) => void,
 *  getSidebarFocusables: () => HTMLElement[],
 *  sidebarOnFocusCapture: (e: React.FocusEvent) => void,
 *  topBarOnFocusCapture: (e: React.FocusEvent) => void,
 *  wasLastInteractionKeyboardRef: React.MutableRefObject<boolean>,
 * }}
 */
const useWingsAppNavKeyboardNavigation = ({
  sidebarRef,
  topBarRef,
  sidebarToggleRef,
  sidebarState,
}) => {
  const sidebarFocusMemory = useFocusMemory()
  const topBarFocusMemory = useFocusMemory()
  const wasLastInteractionKeyboardRef = useRef(false)

  const getSidebarFocusables = useCallback(() => {
    return getVisibleFocusableElements(sidebarRef.current, 'a[href], button:not([disabled])')
  }, [sidebarRef])

  const getTopBarFocusables = useCallback(() => {
    return getVisibleFocusableElements(topBarRef.current, 'a[href], button:not([disabled])')
  }, [topBarRef])

  const getActionPlanFocusables = useCallback(() => {
    if (typeof document === 'undefined') return []

    const actionPlan = document.querySelector('.roadmap-action-plan')
    if (!actionPlan) return []

    const focusables = []
    const headingTarget = document.querySelector('#roadmap-action-plan-focus-target')
    if (headingTarget instanceof HTMLElement && headingTarget.offsetParent !== null) {
      focusables.push(headingTarget)
    }

    const dropdowns = Array.from(actionPlan.querySelectorAll('.wings-dropdown'))
    dropdowns.forEach((dropdown) => {
      if (!(dropdown instanceof HTMLElement) || dropdown.offsetParent === null) return

      const header = dropdown.querySelector('.wings-dropdown-header[role="button"]')
      if (header instanceof HTMLElement && header.offsetParent !== null) {
        focusables.push(header)
      }

      const isOpen = dropdown.classList.contains('wings-dropdown--open')
      if (!isOpen) return

      const contentFocusables = getVisibleFocusableElements(dropdown, '.wings-dropdown-content ' + DEFAULT_CLICKABLE_SELECTOR)
      focusables.push(...contentFocusables)
    })

    return focusables
  }, [])

  const getFeaturedStoriesFocusables = useCallback(() => {
    if (typeof document === 'undefined') return []

    const section = document.querySelector('.roadmap-featured-stories')
    if (!section) return []

    const focusables = []
    const sectionTarget = document.querySelector('#roadmap-featured-stories-focus-target')
    if (sectionTarget instanceof HTMLElement && sectionTarget.offsetParent !== null) {
      focusables.push(sectionTarget)
    }

    const interactive = getVisibleFocusableElements(section, DEFAULT_CLICKABLE_SELECTOR)
    return [...focusables, ...interactive.filter((el) => el.id !== 'roadmap-featured-stories-focus-target')]
  }, [])

  const getFooterFocusables = useCallback(() => {
    if (typeof document === 'undefined') return []

    const footer = document.querySelector('.public-footer')
    if (!(footer instanceof HTMLElement) || footer.offsetParent === null) return []

    return getVisibleFocusableElements(footer, DEFAULT_CLICKABLE_SELECTOR)
  }, [])

  const focusSidebarPreferred = useCallback(() => {
    return sidebarFocusMemory.focusPreferred(getSidebarFocusables())
  }, [getSidebarFocusables, sidebarFocusMemory])

  const focusTopBarPreferred = useCallback(() => {
    const focusables = getTopBarFocusables()
    if (!focusables.length) return false

    const nonToggleFocusables = focusables.filter((el) => el !== sidebarToggleRef.current)
    const pool = nonToggleFocusables.length ? nonToggleFocusables : focusables
    return topBarFocusMemory.focusPreferred(pool)
  }, [getTopBarFocusables, sidebarToggleRef, topBarFocusMemory])

  const focusMainContentStart = useCallback(() => {
    if (typeof document === 'undefined') return false

    const actionPlanTarget = document.querySelector('#roadmap-action-plan-focus-target')
    if (actionPlanTarget instanceof HTMLElement && actionPlanTarget.offsetParent !== null) {
      actionPlanTarget.focus()
      return true
    }

    const mainRoot = document.querySelector('main, [role="main"], #___gatsby, #gatsby-focus-wrapper')
    if (!mainRoot) return false

    const firstFocusable = mainRoot.querySelector(
      '[data-main-content-focus-start="true"], ' + DEFAULT_CLICKABLE_SELECTOR
    )

    if (!(firstFocusable instanceof HTMLElement) || firstFocusable.offsetParent === null) return false
    firstFocusable.focus()
    return true
  }, [])

  const focusFeaturedStoriesStart = useCallback(() => {
    const focusables = getFeaturedStoriesFocusables()
    if (!focusables.length) return false
    focusables[0].focus()
    return true
  }, [getFeaturedStoriesFocusables])

  const focusActionPlanEnd = useCallback(() => {
    const focusables = getActionPlanFocusables()
    if (!focusables.length) return false
    focusables[focusables.length - 1].focus()
    return true
  }, [getActionPlanFocusables])

  const focusFeaturedStoriesEnd = useCallback(() => {
    const focusables = getFeaturedStoriesFocusables()
    if (!focusables.length) return false
    focusables[focusables.length - 1].focus()
    return true
  }, [getFeaturedStoriesFocusables])

  const focusFooterStart = useCallback(() => {
    const focusables = getFooterFocusables()
    if (!focusables.length) return false
    focusables[0].focus()
    return true
  }, [getFooterFocusables])

  const handleSidebarKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        focusMainContentStart()
        return
      }

      createLinearArrowKeyHandler({
        getFocusables: getSidebarFocusables,
        nextKey: 'ArrowDown',
        prevKey: 'ArrowUp',
        wrap: true,
        onPrevBoundary: () => {
          sidebarToggleRef.current?.focus()
          return true
        },
      })(e)
    },
    [focusMainContentStart, getSidebarFocusables, sidebarToggleRef]
  )

  const handleTopBarKeyDown = useCallback(
    (e) => {
      if (e.target?.closest?.('.wings-app-nav-search-container')) return
      if (e.target?.closest?.('.notifications-dropdown') && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) return

      if (e.key === 'ArrowDown' && document.activeElement === sidebarToggleRef.current) {
        e.preventDefault()
        getSidebarFocusables()[0]?.focus()
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        focusMainContentStart()
        return
      }

      createLinearArrowKeyHandler({
        getFocusables: getTopBarFocusables,
        nextKey: 'ArrowRight',
        prevKey: 'ArrowLeft',
        wrap: true,
      })(e)
    },
    [focusMainContentStart, getSidebarFocusables, getTopBarFocusables, sidebarToggleRef]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const onKey = () => {
      wasLastInteractionKeyboardRef.current = true
    }

    const onPointer = () => {
      wasLastInteractionKeyboardRef.current = false
    }

    window.addEventListener('keydown', onKey, true)
    window.addEventListener('pointerdown', onPointer, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('pointerdown', onPointer, true)
    }
  }, [])

  const getRegionForElement = useCallback((activeElement) => {
    if (activeElement.closest('.roadmap-action-plan')) return 'actionPlan'
    if (activeElement.closest('.roadmap-featured-stories')) return 'featuredStories'
    if (activeElement.closest('.public-footer')) return 'footer'
    return null
  }, [])

  const getFocusablesForRegion = useCallback(
    (region) => {
      if (region === 'actionPlan') return getActionPlanFocusables()
      if (region === 'featuredStories') return getFeaturedStoriesFocusables()
      if (region === 'footer') return getFooterFocusables()
      return []
    },
    [getActionPlanFocusables, getFeaturedStoriesFocusables, getFooterFocusables]
  )

  const handleKeyForRegion = useCallback(
    ({ region, key, focusables, index }) => {
      if (region === 'actionPlan' && key === 'ArrowLeft') {
        focusSidebarPreferred()
        return true
      }

      if (region === 'actionPlan' && key === 'ArrowRight') {
        if (!focusFeaturedStoriesStart()) {
          focusables[Math.max(0, index)]?.focus()
        }
        return true
      }

      if (region === 'footer' && key === 'ArrowLeft') {
        focusSidebarPreferred()
        return true
      }

      return false
    },
    [focusFeaturedStoriesStart, focusSidebarPreferred]
  )

  const onBoundary = useCallback(
    ({ region, key, focusables }) => {
      if (region === 'actionPlan' && key === 'ArrowDown') {
        if (!focusFeaturedStoriesStart() && !focusFooterStart()) {
          focusables[focusables.length - 1]?.focus()
        }
        return true
      }

      if (region === 'actionPlan' && key === 'ArrowUp') {
        focusTopBarPreferred()
        return true
      }

      if (region === 'featuredStories' && key === 'ArrowUp') {
        if (!focusActionPlanEnd()) {
          focusTopBarPreferred()
        }
        return true
      }

      if (region === 'featuredStories' && key === 'ArrowLeft') {
        if (!focusActionPlanEnd()) {
          focusSidebarPreferred()
        }
        return true
      }

      if (region === 'featuredStories' && key === 'ArrowDown') {
        if (!focusFooterStart()) {
          focusables[focusables.length - 1]?.focus()
        }
        return true
      }

      if (region === 'footer' && key === 'ArrowUp') {
        if (!focusFeaturedStoriesEnd() && !focusActionPlanEnd()) {
          focusTopBarPreferred()
        }
        return true
      }

      return false
    },
    [
      focusActionPlanEnd,
      focusFeaturedStoriesEnd,
      focusFeaturedStoriesStart,
      focusFooterStart,
      focusSidebarPreferred,
      focusTopBarPreferred,
    ]
  )

  const shouldWrapOnBoundary = useCallback(({ region, key }) => {
    if (region === 'featuredStories' && key === 'ArrowRight') {
      return true
    }
    return false
  }, [])

  useDirectionalRegionNavigation({
    isEnabled: !!sidebarState,
    getRegionForElement,
    getFocusablesForRegion,
    handleKeyForRegion,
    onBoundary,
    shouldWrapOnBoundary,
    isEditableElement: isKeyboardEditableElement,
  })

  return {
    handleSidebarKeyDown,
    handleTopBarKeyDown,
    getSidebarFocusables,
    sidebarOnFocusCapture: sidebarFocusMemory.onFocusCapture,
    topBarOnFocusCapture: topBarFocusMemory.onFocusCapture,
    wasLastInteractionKeyboardRef,
  }
}

export default useWingsAppNavKeyboardNavigation
